/**
 * Two small guards that protect the offline-capture path against the
 * two ways data can disappear under the user's feet:
 *
 *   1. Storage near full — once IndexedDB usage exceeds 80% of the
 *      origin's quota, photos and session writes are at imminent risk
 *      of failing. Show a warning banner so the controller can sync
 *      or take fewer photos before they hit the limit. (§5.8.7)
 *
 *   2. Tab close with pending sessions — `beforeunload` prompts the
 *      user before navigating away while sessions are unfinished or
 *      waiting to sync. Doesn't prevent force-close or battery death,
 *      but mitigates the common "I'll close this tab and start fresh"
 *      mistake. (§5.8.8)
 *
 * Mounted as a no-render component inside <ConnectivityProvider>'s
 * subtree so it has access to `useConnectivity()` for `pendingSessions`
 * and so it can be activated lazily for tenants with the master flag
 * on.
 */

import { useEffect, useState } from "react";
import { Alert } from "reactstrap";

import useConfig from "../actions/Tenants/config/configHook";
import { useConnectivity } from "./connectivityMonitor";
import { isOfflineChecksEnabled } from "./db";
import {
    estimateStorageUsage,
    getActiveSessionId,
} from "./sessionManager";

const STORAGE_WARN_THRESHOLD = 0.8;
const STORAGE_POLL_INTERVAL_MS = 30_000;

/**
 * Polls navigator.storage.estimate() every 30s and exposes a boolean
 * for "approaching the IndexedDB quota". Defaults to false in tenants
 * without `enable_offline_checks` (no point measuring) and on
 * platforms where the API isn't available.
 */
export function useStorageWarning(): boolean {
    const config = useConfig();
    const enabled = isOfflineChecksEnabled(config);
    const [warn, setWarn] = useState(false);

    useEffect(() => {
        if (!enabled) {
            setWarn(false);
            return undefined;
        }
        let cancelled = false;
        const tick = async () => {
            const fraction = await estimateStorageUsage();
            if (cancelled) return;
            setWarn(fraction !== null && fraction > STORAGE_WARN_THRESHOLD);
        };
        tick();
        const id = setInterval(tick, STORAGE_POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [enabled]);

    return warn;
}

/**
 * Renders the storage-warning banner when the device is approaching
 * its IndexedDB quota. No-op when the master flag is off or the
 * platform doesn't expose `navigator.storage.estimate()`.
 */
export function OfflineStorageWarning(): JSX.Element | null {
    const warn = useStorageWarning();
    if (!warn) return null;
    return (
        <Alert color="warning" className="text-center py-1 fs-7 mb-0 rounded-0">
            Device storage is almost full. Consider syncing or taking fewer photos.
        </Alert>
    );
}

/**
 * beforeunload guard that prompts the user when there are unfinished
 * or unsynced offline sessions. The exact wording is dictated by the
 * browser (most ignore the custom message and show their own); we just
 * need to set `returnValue` to anything truthy.
 *
 * Pending criteria:
 *   - There's an active session in `collecting` (the user is mid-form
 *     — getActiveSessionId() returns non-null), OR
 *   - There are sessions queued in `ready_to_sync` (pendingSessions
 *     from the connectivity context).
 *
 * Wires up the `beforeunload` listener once on mount; tears down on
 * unmount. Mount this once at the App level (next to IsOfflineBar).
 */
export function OfflineUnloadGuard(): null {
    const config = useConfig();
    const enabled = isOfflineChecksEnabled(config);
    const { pendingSessions } = useConnectivity();

    useEffect(() => {
        if (!enabled) return undefined;
        const handler = (event: BeforeUnloadEvent) => {
            const hasActive = getActiveSessionId() !== null;
            if (!hasActive && pendingSessions === 0) return undefined;
            event.preventDefault();
            // Most browsers ignore this string and show their own
            // localized prompt, but setting `returnValue` (the
            // legacy field) is still required to trigger the dialog.
            const message = "You have unsynced check data. Are you sure you want to leave?";
            event.returnValue = message;
            return message;
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [enabled, pendingSessions]);

    return null;
}
